import { ReactNode, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './StickyFilterBarRow.module.scss';

const HEIGHT_VAR = '--plp-sticky-filters-height';

type StickyFilterBarRowProps = {
  children: ReactNode;
  className?: string;
  /** Changes whenever the applied filters change. */
  resetKey?: string;
};

/**
 * Pins the PLP filter row under the site header once it's scrolled past
 * (ED-531). Sticky positioning keeps the row's space in the flow, so nothing
 * below it moves when it pins or releases.
 */
const StickyFilterBarRow = ({
  children,
  className,
  resetKey,
}: StickyFilterBarRowProps) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const previousResetKey = useRef(resetKey);

  // The sentinel marks the row's resting position: once it has scrolled above
  // the row's sticky `top`, the row is pinned.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const row = rowRef.current;
    if (!sentinel || !row) return undefined;

    let frame: number | null = null;
    const update = () => {
      frame = null;
      const stickyTop = parseFloat(getComputedStyle(row).top) || 0;
      setIsStuck(sentinel.getBoundingClientRect().top < stickyTop);
    };
    const onScroll = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  // Shallow routing keeps the scroll position, so a filter applied from the
  // pinned row would leave the reader wherever they were in a now-shorter
  // grid, often the footer. Bring the row back to its resting position so the
  // new results start right under it. Scrolling up slides the site header back
  // in, so aim below its full height: the row's sticky `top` never exceeds it.
  useEffect(() => {
    if (previousResetKey.current === resetKey) return;
    previousResetKey.current = resetKey;

    const sentinel = sentinelRef.current;
    const row = rowRef.current;
    if (!sentinel || !row) return;

    const sentinelTop = sentinel.getBoundingClientRect().top;
    const stickyTop = parseFloat(getComputedStyle(row).top) || 0;
    if (sentinelTop >= stickyTop) return;

    const headerHeight =
      parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--site-header-height'
        )
      ) || 0;
    window.scrollTo({
      top: window.scrollY + sentinelTop - headerHeight,
      behavior: 'smooth',
    });
  }, [resetKey]);

  // Grid tiles use this for `scroll-margin-top`, so a keyboard-focused tile
  // scrolls into view below the row instead of underneath it.
  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;

    const root = document.documentElement;
    const updateHeight = () =>
      root.style.setProperty(HEIGHT_VAR, `${row.offsetHeight}px`);

    updateHeight();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateHeight);
      return () => {
        window.removeEventListener('resize', updateHeight);
        root.style.removeProperty(HEIGHT_VAR);
      };
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(row);

    return () => {
      resizeObserver.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, []);

  return (
    <>
      <div aria-hidden className={styles.sentinel} ref={sentinelRef} />
      <div
        ref={rowRef}
        className={cn(className, styles.sticky, { [styles.stuck]: isStuck })}
        data-stuck={isStuck || undefined}
        // The site header holds still while a pill's dropdown is open here.
        data-pinned-under-header
      >
        {children}
      </div>
    </>
  );
};

export default StickyFilterBarRow;
